import { useState, useEffect } from "react";
import axios from "axios";
import "@/index.css";
import { BrowserRouter, Routes, Route, NavLink, useLocation, Navigate } from "react-router-dom";
import { Toaster } from 'sonner';
import {
  LayoutDashboard, FileText, Megaphone, Settings,
  ChevronLeft, ChevronRight, Sparkles, Zap, Facebook, Instagram,
  CheckCircle, FileEdit, CalendarDays, RefreshCw,
  TrendingUp, Share2, Lightbulb, Edit, Check, HelpCircle, BarChart3, Inbox, User, History, Wifi, Tag, Globe, Video, Gift,
  MessageSquareDot, LogOut, Link2, Coins
} from "lucide-react";
import { REQUIRE_AUTH, isAuthenticated, logout, getStoredUser, hydratePlanFromServer } from "@/lib/authToken";
import { useMasterStatus } from "@/hooks/useIsMaster";
import LanguageToggle from "@/components/LanguageToggle";
import { isAdminView, deriveMasterState } from "@/lib/masterViewModell";

// Extracted Pages
import SimpleAssistentPage from './pages/SimpleAssistentPage';
import SettingsPage from './pages/SettingsPage';
import StrategieExpertePage from './pages/StrategieExpertePage';
import DatenbasisPage from './pages/DatenbasisPage';
import MonthlyPlanPage, { Campaigns, MediaLibrary } from './pages/MonthlyPlanPage';
import AnalyticsPage from './pages/AnalyticsPage';
import EvergreenPage from './pages/EvergreenPage';
import InboxPage from './pages/InboxPage';
import ProfileOptimizerPage from './pages/ProfileOptimizerPage';
import DatenschutzPage from './pages/DatenschutzPage';
import NutzungsbedingungenPage from './pages/NutzungsbedingungenPage';
import HilfePage from './pages/HilfePage';
import NotFoundPage from './pages/NotFoundPage';
import SocialMediaManagerPage from './pages/SocialMediaManagerPage';
import MoltbookIntegrationPage from './pages/MoltbookIntegrationPage';
import InstagramManagerPage from './pages/InstagramManagerPage';
import SetupGuidePage from './pages/SetupGuidePage';
import MasterpostPage from './pages/MasterpostPage';
import ContentToolsPage from './pages/ContentToolsPage';
import StrategieHistoryPage from './pages/StrategieHistoryPage';
import SharePage from './pages/SharePage';
import DashboardPage from './pages/DashboardPage';
import ContentGeneratorPage from './pages/ContentGeneratorPage';
import SocialConnectPage from './pages/SocialConnectPage';
import PricingPage from './pages/PricingPage';
import PlanfinderPage from './pages/PlanfinderPage';
import KontaktPage from './pages/KontaktPage';
import WebsiteOptimierungPage from './pages/WebsiteOptimierungPage';
import VideoPage from './pages/VideoPage';
import BonusCenterPage from './pages/BonusCenterPage';
import GeldVerdienenPage from './pages/GeldVerdienenPage';
import KeywordDMPage from './pages/KeywordDMPage';
import InstagramCommentDMPage from './pages/InstagramCommentDMPage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LandingPage from './pages/LandingPage';
import ImpressumPage from './pages/ImpressumPage';
import VertrauenPage from './pages/VertrauenPage';
import LinkInBioPage from './pages/LinkInBioPage';
import LinkInBioPublicPage from './pages/LinkInBioPublicPage';
import OnboardingWizard from './components/OnboardingWizard';
import ProductHeader from './components/ProductHeader';
import { FacebookPreviewModal, LinkedInPreviewModal, OutputFormatModal, TrendResearchModal, ContentRow } from './components/PostModals';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";


// ── Role management ──────────────────────────────────────────────────────────
// Die Rolle kommt aus dem Backend-Account (is_master). Der Inhaber-/Master-Account
// behält volle Admin-Sicht; echte Kunden-Accounts (künftig role="customer") sehen
// nur die schlanke Kunden-Ansicht. localStorage speichert nur die VIEW-Präferenz
// des Masters (Vorschau der Kunden-Ansicht), nicht die Rolle selbst.
const VIEW_KEY = 'foerderfuchs_view';
const getStoredView = () => localStorage.getItem(VIEW_KEY) || 'admin';
const saveView = (view) => localStorage.setItem(VIEW_KEY, view);

// ── Sidebar Component ──────────────────────────────────────────────────────
// `overlay` = true → mobiles Menü (volle Höhe ab oben, mit Marken-Kopf).
// `overlay` = false → Desktop-Werkzeugleiste, sitzt UNTER dem globalen Header.
const Sidebar = ({ collapsed, setCollapsed, overlay = false }) => {
  const location = useLocation();
  // isMaster: aus dem Backend. FAIL-CLOSED (null = Antwort steht noch aus):
  // Ein Kunde darf das Admin-Menü nie sehen — auch nicht für die Sekunde bis
  // /api/account/me antwortet und auch nicht, wenn der Aufruf fehlschlägt.
  // Bewusste Abwägung: Bei einem API-Ausfall verliert der Inhaber kurzzeitig
  // seine Zusatz-Werkzeuge. Das ist gewollt.
  const [isMaster, setIsMaster] = useState(null);
  const [viewMode, setViewMode] = useState(getStoredView);

  useEffect(() => {
    let active = true;
    axios.get(`${BACKEND_URL}/api/account/me`)
      .then(res => {
        if (!active) return;
        const { isMaster: master, forceViewToUser } = deriveMasterState(res.data);
        setIsMaster(master);
        if (forceViewToUser) { setViewMode('user'); saveView('user'); }
      })
      .catch(() => { if (active) setIsMaster(false); /* fail-closed */ });
    return () => { active = false; };
  }, []);

  const toggleRole = () => {
    const next = viewMode === 'admin' ? 'user' : 'admin';
    saveView(next);
    setViewMode(next);
  };

  // Admin-Sicht nur für den Master UND nur wenn er nicht in die Kunden-Vorschau wechselt.
  // isMaster === null (Antwort steht aus) zählt als „kein Master". Regel + Tests:
  // lib/masterViewModell.js.
  const isAdmin = isAdminView(isMaster, viewMode);

  // ── Kunden-Kernmenü (7 Punkte, Reihenfolge = Kundenreise, Kundensprache) ──────
  const coreNavItems = [
    { path: '/assistent',    icon: Sparkles,     label: 'Start',             badge: 'START', highlight: true },
    { path: '/',             icon: LayoutDashboard, label: 'Übersicht' },
    { path: '/kanale',       icon: Wifi,         label: 'Kanäle verbinden' },
    { path: '/monthly-plan', icon: CalendarDays, label: 'Monatsplan',        badge: '1 KLICK' },
    // '/calendar' bewusst entfernt: die alte Wochenansicht liest die Collection
    // `content`, die es in der Produktion nicht gibt — Kunden sahen dort immer
    // eine leere Woche. Der Monatsplan ist die einzige Plan-Ansicht.
    { path: '/analytics',    icon: BarChart3,    label: 'Auswertung' },
    { path: '/link-in-bio',  icon: Link2,        label: 'Link-in-Bio',       badge: 'NEU' },
    // Gehört bewusst ins KERNmenü, nicht zu adminNavItems: dort steht bereits
    // '/bonus' ("Bonus & Empfehlungen"), das für normale Kunden unsichtbar ist,
    // weil adminNavItems nur bei isAdmin gerendert wird. Ein Verdienst-Angebot,
    // das der Kunde nie sieht, ist keins.
    { path: '/geld-verdienen', icon: Coins,      label: 'Geld verdienen',    badge: 'NEU' },
    { path: '/settings',     icon: Settings,     label: 'Einstellungen' },
  ];

  // ── Hilfs-Punkte (dezent, unter dem Kernmenü) ────────────────────────────────
  const utilityNavItems = [
    { path: '/pricing', icon: Tag,        label: 'Preise & Pakete' },
    { path: '/hilfe',   icon: HelpCircle, label: 'Hilfe' },
  ];

  // Items only the admin/master sees (under a divider) — grouped by section
  // Items with `section` property render a group header before them.
  const adminNavItems = [
    // ── Erstellen ──────────────────────────────────────────────────────────
    { section: 'Erstellen' },
    { path: '/strategie',       icon: TrendingUp, label: 'Strategie-Experte' },
    { path: '/generator',       icon: Sparkles,   label: 'Content Generator' },
    { path: '/masterpost',      icon: FileEdit,   label: 'Masterpost' },
    { path: '/social-media',    icon: Share2,     label: 'Multi-Channel' },
    { path: '/instagram',       icon: Instagram,  label: 'Instagram Manager' },
    { path: '/content-tools',   icon: Lightbulb,  label: 'Content Tools' },
    { path: '/video',           icon: Video,      label: 'Video erstellen' },
    // ── Planen & Archivieren ───────────────────────────────────────────────
    { section: 'Planen & Archivieren' },
    { path: '/campaigns',       icon: Megaphone,  label: 'Kampagnen' },
    { path: '/evergreen',       icon: RefreshCw,  label: 'Evergreen' },
    { path: '/strategie-history', icon: History,  label: 'Strategie-Historie' },
    // ── Kunde & Community ──────────────────────────────────────────────────
    { section: 'Kunde & Community' },
    { path: '/inbox',           icon: Inbox,           label: 'Social Inbox' },
    { path: '/keyword-dm',     icon: MessageSquareDot, label: 'Kommentar → DM', badge: 'NEU' },
    { path: '/bonus',           icon: Gift,       label: 'Bonus & Empfehlungen' },
    // ── Setup & Profil ─────────────────────────────────────────────────────
    { section: 'Setup & Profil' },
    { path: '/datenbasis',           icon: FileText,    label: 'Datenbasis' },
    { path: '/moltbook',             icon: Zap,         label: 'Moltbook' },
    { path: '/website-optimierung',  icon: Globe,       label: 'Website-Optimierung', badge: 'NEU' },
    { path: '/profile-optimizer',    icon: User,        label: 'Profile Optimizer',   badge: 'NEU' },
    { path: '/setup-guide',          icon: CheckCircle, label: 'Setup & Hilfe' },
  ];

  const renderNavItem = (item, index) => {
    // Section header — rendered as group label in expanded mode, thin divider when collapsed
    if (item.section) {
      return (
        <div key={`section-${index}`} className={`pt-3 pb-1 ${collapsed ? 'px-1' : 'px-2'}`}>
          {collapsed ? (
            <div className="border-t border-stone-100" />
          ) : (
            <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider px-1">
              {item.section}
            </p>
          )}
        </div>
      );
    }

    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    return (
      <NavLink
        key={item.path}
        to={item.path}
        data-testid={`nav-${item.label.toLowerCase().replace(/[\s/&]+/g, '-')}`}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
          item.highlight && !isActive
            ? 'brand-gradient text-white shadow-sm shadow-orange-100'
            : isActive
              ? 'bg-fox-fire text-white shadow-sm shadow-orange-100'
              : 'text-stone-600 hover:bg-fox-tint hover:text-stone-900'
        }`}
      >
        <Icon size={18} className="flex-shrink-0" />
        {!collapsed && (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="font-medium text-sm truncate">{item.label}</span>
            {item.badge && (
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
                item.highlight ? 'bg-white/20 text-white' : 'bg-brand-indigo text-white'
              }`}>
                {item.badge}
              </span>
            )}
          </div>
        )}
      </NavLink>
    );
  };

  return (
    <aside
      data-testid="sidebar"
      className={`bg-white border-r border-stone-200 flex flex-col transition-all duration-300 ${
        overlay
          ? 'fixed left-0 top-0 h-full z-50 w-72'
          : `fixed left-0 top-[109px] lg:top-[121px] bottom-0 z-30 ${collapsed ? 'w-16' : 'w-64'}`
      }`}
    >
      {/* Kopf: im Overlay das Marken-Lockup, auf dem Desktop ein schlanker Titel
          (der globale Header trägt dort bereits Logo + Wortmarke). */}
      {overlay ? (
        <div className="h-16 flex items-center gap-2.5 px-4 border-b border-stone-200 flex-shrink-0">
          <img src="/images/logo.png" alt="Förderfuchs" width="240" height="276" className="h-8 w-auto" />
          <span className="flex flex-col leading-tight">
            <span className="font-heading font-extrabold text-stone-900 text-sm">Social Media Manager</span>
            <span className="text-[10px] text-stone-500 font-medium">by Förderfuchs</span>
          </span>
        </div>
      ) : (
        !collapsed && (
          <div className="px-4 pt-4 pb-1 flex-shrink-0">
            <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider">Werkzeuge</p>
          </div>
        )
      )}

      {/* Navigation – scrollable area */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {/* Kunden-Kernmenü */}
        {coreNavItems.map(renderNavItem)}

        {/* Hilfs-Punkte (dezent abgesetzt) */}
        <div className={`pt-3 pb-1 ${collapsed ? 'px-1' : 'px-2'}`}>
          <div className="border-t border-stone-100" />
        </div>
        {utilityNavItems.map(renderNavItem)}

        {/* Admin section */}
        {isAdmin && (
          <>
            <div className={`pt-3 pb-1 ${collapsed ? 'px-1' : 'px-2'}`}>
              {collapsed ? (
                <div className="border-t border-stone-200" />
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex-1 border-t border-stone-200" />
                  <span className="text-xs text-stone-400 font-medium whitespace-nowrap">Admin</span>
                  <div className="flex-1 border-t border-stone-200" />
                </div>
              )}
            </div>
            {adminNavItems.map((item, idx) => renderNavItem(item, idx))}
          </>
        )}
      </nav>

      {/* Bottom: Role toggle + Collapse button */}
      <div className={`flex-shrink-0 border-t border-stone-200 p-3 space-y-2`}>
        {/* Master-Hinweis – nur für den Inhaber, in Admin-Sicht, nicht eingeklappt */}
        {isMaster && isAdmin && !collapsed && (
          <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 leading-snug">
            Master-Ansicht: Du siehst alle Werkzeuge. Kunden sehen nur die schlanke
            Kunden-Ansicht (Start, Übersicht, Kanäle, Monatsplan, Auswertung,
            Link-in-Bio, Einstellungen).
          </p>
        )}

        {/* Ansicht-Umschalter — nur für den Master (Kunden-Vorschau) */}
        {isMaster && (
          <button
            onClick={toggleRole}
            data-testid="role-toggle-btn"
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
              isAdmin
                ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
            }`}
            title={isAdmin ? 'Zu Kunden-Ansicht wechseln' : 'Zu Master-Ansicht wechseln'}
          >
            {isAdmin ? (
              <>
                <span className="w-4 h-4 rounded-full bg-amber-400 flex-shrink-0 flex items-center justify-center">
                  <span className="text-white" style={{ fontSize: 9 }}>M</span>
                </span>
                {!collapsed && <span>Master-Ansicht</span>}
              </>
            ) : (
              <>
                <span className="w-4 h-4 rounded-full bg-stone-400 flex-shrink-0 flex items-center justify-center">
                  <span className="text-white" style={{ fontSize: 9 }}>K</span>
                </span>
                {!collapsed && <span>Kunden-Vorschau</span>}
              </>
            )}
          </button>
        )}

        {/* Sprachumschalter — immer sichtbar, unabhängig von Rolle */}
        {!collapsed && (
          <div className="flex justify-center">
            <LanguageToggle />
          </div>
        )}

        {/* Session: eingeloggter Nutzer + Abmelden (nur wenn angemeldet bzw. SaaS-Modus) */}
        <SessionBox collapsed={collapsed} />

        {/* Collapse button */}
        <button
          data-testid="sidebar-toggle"
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center py-1.5 rounded-lg hover:bg-stone-100 text-stone-400"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
    </aside>
  );
};

// ── Session-/Logout-Box ──────────────────────────────────────────────────────
// Zeigt den angemeldeten Nutzer + "Abmelden". Sichtbar, sobald ein Token vorliegt
// (oder generell im SaaS-Modus). Im reinen Internbetrieb ohne Login bleibt sie aus.
const SessionBox = ({ collapsed }) => {
  const loggedIn = isAuthenticated();
  if (!loggedIn && !REQUIRE_AUTH) return null;

  const user = getStoredUser();
  const email = user?.email || "";

  const handleLogout = async () => {
    try { await logout(); } finally {
      try { localStorage.removeItem("ff_onboarding_done"); } catch (_) {}
      window.location.assign("/login");
    }
  };

  if (!loggedIn) return null;

  return (
    <div className="border-t border-stone-100 pt-2">
      {!collapsed && email && (
        <p className="text-[11px] text-stone-400 px-1 mb-1 truncate" title={email}>
          Angemeldet als <span className="text-stone-600">{email}</span>
        </p>
      )}
      <button
        onClick={handleLogout}
        data-testid="logout-btn"
        title="Abmelden"
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-stone-500 hover:bg-stone-100 hover:text-red-600 transition-all"
      >
        <LogOut size={16} className="flex-shrink-0" />
        {!collapsed && <span>Abmelden</span>}
      </button>
    </div>
  );
};

// ── MasterOnlyRoute — Routen, die nur der Inhaber sehen darf ─────────────────
// Fail-closed: Solange /api/account/me nicht geantwortet hat, wird NICHTS gerendert
// (kein Redirect — sonst flöge der Inhaber beim Direktaufruf raus, bevor die Antwort
// da ist). Kein Master → zurück auf die Übersicht.
const MasterOnlyRoute = ({ children }) => {
  const status = useMasterStatus();
  if (status === null) return null;
  if (status !== true) return <Navigate to="/" replace />;
  return children;
};

// ── ScrollToHash — springt Loop-Anker (#markenprofil / #veroeffentlichen) sanft an ──
// Der fixierte Header verdeckt sonst das Ziel; die Anker tragen scroll-mt (siehe Seiten).
const ScrollToHash = () => {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    // kurzer Timeout, damit die Zielseite gerendert ist
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(t);
  }, [hash, pathname]);
  return null;
};

// ── Footer mit Pflicht-Rechtslinks (auf jeder Seite) ─────────────────────────
const AppFooter = () => (
  <footer className="px-4 md:px-8 lg:px-12 py-6 mt-4 border-t border-stone-100">
    <div className="max-w-7xl flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-stone-400">
      <span>© {new Date().getFullYear()} Förderfuchs</span>
      <NavLink to="/impressum" className="hover:text-stone-600">Impressum</NavLink>
      <NavLink to="/datenschutz" className="hover:text-stone-600">Datenschutz</NavLink>
      <NavLink to="/vertrauen" className="hover:text-stone-600">Vertrauen &amp; DSGVO</NavLink>
      <NavLink to="/nutzungsbedingungen" className="hover:text-stone-600">AGB</NavLink>
      {(!REQUIRE_AUTH || isAuthenticated()) && (
        <NavLink to="/hilfe" className="hover:text-stone-600">Hilfe</NavLink>
      )}
    </div>
  </footer>
);

// ── Inner Layout — nutzt useLocation, muss innerhalb BrowserRouter sein ──────

function MainLayout() {
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen]     = useState(false);

  // ── Onboarding-Check ──────────────────────────────────────────────────────
  // Liest aus localStorage (schnell) und verifiziert mit Backend (einmalig).
  // Bei API-Fehler: kein Wizard — Nutzer wird nie eingesperrt.
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Beim App-Start (bestehendes Token, evtl. neuer Browser) den echten Tarif/Trial vom
  // Server holen und in den localStorage spiegeln — sonst zeigen die Feature-Gates auf
  // einem frischen Gerät fälschlich „gesperrt". Einmalig, best-effort.
  useEffect(() => {
    if (REQUIRE_AUTH && !isAuthenticated()) return;
    hydratePlanFromServer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Öffentliche Seiten ohne Login (SharePage, Link-in-Bio /p/:slug, Landing/Pricing/Recht)
    // bekommen nie den Wizard — und dürfen KEIN /api/account/me auslösen: das wäre für anonyme
    // Besucher ein 401 und der Axios-Interceptor würde sie auf /login werfen (kaputtes Schaufenster).
    if (location.pathname.startsWith("/share/") || location.pathname.startsWith("/p/")) return;
    if (REQUIRE_AUTH && !isAuthenticated()) return;

    // Schnell-Prüfung via Cache
    if (localStorage.getItem("ff_onboarding_done") === "1") return;
    // In dieser Sitzung bereits weggeklickt? Nicht erneut öffnen (der Abschluss-POST
    // ist best-effort — schlägt er fehl, würde der Wizard sonst bei jeder Navigation
    // wieder aufpoppen).
    try { if (sessionStorage.getItem("ff_onboarding_dismissed") === "1") return; } catch (_) {}

    const checkOnboarding = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/account/me`);
        if (res.data?.onboarding_completed === false) {
          setShowOnboarding(true);
        } else {
          // Bereits abgeschlossen — cachen
          localStorage.setItem("ff_onboarding_done", "1");
        }
      } catch (_) {
        // API nicht erreichbar → Wizard nicht zeigen, Flow nicht blockieren
      }
    };
    checkOnboarding();
  // Auf location.pathname reagieren: nach der Registrierung navigiert LoginPage per SPA
  // aufs Dashboard OHNE MainLayout-Remount — ohne diese Abhängigkeit liefe der Check nie
  // erneut und der Neukunde sähe den Onboarding-Wizard erst nach manuellem Reload.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Mobiles Menü bei Navigation schließen (sonst bliebe das Overlay über der neuen Seite)
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  // Escape schließt das mobile Menü (Tastatur-Bedienbarkeit)
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMobileMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileMenuOpen]);

  // SharePage bekommt ein komplett eigenes, schlankes Layout (kein Sidebar/Header)
  if (location.pathname.startsWith("/share/")) {
    return (
      <Routes>
        <Route path="/share/:token" element={<SharePage />} />
      </Routes>
    );
  }

  // Öffentliche Link-in-Bio-Seite — ohne Login, eigenes mobiles Layout (vor dem Auth-Gate).
  if (location.pathname.startsWith("/p/")) {
    return (
      <Routes>
        <Route path="/p/:slug" element={<LinkInBioPublicPage />} />
      </Routes>
    );
  }

  // Login/Registrierung/Passwort-Reset: öffentlich, eigenes Layout (kein Sidebar/Header).
  // Der Reset-Link aus der E-Mail (/passwort-zuruecksetzen) MUSS ohne Anmeldung
  // erreichbar sein — sonst käme ein ausgesperrter Kunde nie wieder rein.
  if (location.pathname.startsWith("/login") ||
      location.pathname.startsWith("/passwort-zuruecksetzen")) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/passwort-zuruecksetzen" element={<ResetPasswordPage />} />
      </Routes>
    );
  }

  // Öffentliches Schaufenster: nicht angemeldete Besucher sehen auf "/" die Landing-Seite
  // (Akquise, vor dem Auth-Gate). Angemeldete sehen unter "/" ihr Dashboard (Routes unten).
  if (REQUIRE_AUTH && !isAuthenticated() && location.pathname === "/") {
    return <LandingPage />;
  }

  // Rechtsseiten MÜSSEN ohne Anmeldung erreichbar sein (Impressum barrierefrei, von
  // der Login-Seite verlinkt). Daher öffentlich, schlankes Layout, VOR dem Auth-Gate.
  // Vertrauen/DSGVO ist bewusst öffentlich: reine, statische Vertrauens-Infos (Kauf-
  // argument vor dem Login) — kein Tenant-/Nutzerbezug. Footer verlinkt darauf überall.
  // Öffentliche Akquise-/Rechtsseiten (vor dem Auth-Gate): Recht ist Pflicht-barrierefrei,
  // Pricing/Planfinder sind Kaufargumente, die ein Interessent VOR der Registrierung sehen soll.
  // Alle rein statisch bzw. (usePlanSelection) ausgeloggt abgesichert — kein Tenant-Bezug.
  const PUBLIC_PAGES = ["/impressum", "/datenschutz", "/nutzungsbedingungen", "/vertrauen", "/pricing", "/planfinder", "/kontakt"];
  if (REQUIRE_AUTH && !isAuthenticated() && PUBLIC_PAGES.includes(location.pathname)) {
    return (
      <div className="min-h-screen bg-stone-50">
        <div className="max-w-6xl mx-auto pt-6 px-4 flex items-center justify-between gap-3">
          <NavLink to="/" className="text-sm text-fox-fire hover:underline">← Zur Startseite</NavLink>
          <NavLink
            to="/login?tab=register"
            className="text-sm font-semibold text-white bg-fox-fire px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
          >
            14 Tage kostenlos starten
          </NavLink>
        </div>
        <div className="max-w-6xl mx-auto px-4 pb-6">
          <Routes>
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="/nutzungsbedingungen" element={<NutzungsbedingungenPage />} />
            <Route path="/vertrauen" element={<VertrauenPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/planfinder" element={<PlanfinderPage />} />
            <Route path="/kontakt" element={<KontaktPage />} />
          </Routes>
        </div>
        <AppFooter />
      </div>
    );
  }

  // Auth-Gate (SaaS-Modus): ohne Anmeldung kein Zugriff auf die App.
  // Im Bestands-/Internbetrieb (REQUIRE_AUTH=false) bleibt alles wie bisher.
  if (REQUIRE_AUTH && !isAuthenticated()) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return (
    <>
      {/* Globaler Produkt-Header (Marke + Loop-Stepper) — auf allen Größen */}
      <ProductHeader onMenuClick={() => setMobileMenuOpen(true)} />

      {/* Desktop Sidebar (Werkzeuge) — sitzt unter dem Header */}
      <div className="hidden lg:block">
        <Sidebar collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} />
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-50" onClick={() => setMobileMenuOpen(false)}>
          <div className="h-full" onClick={e => e.stopPropagation()}>
            <Sidebar collapsed={false} setCollapsed={() => {}} overlay />
          </div>
        </div>
      )}

      {/* Main Content — Abstand für Header (Zeile 1 + Loop-Zeile) und Sidebar */}
      <main className={`transition-all duration-300 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      } pt-[109px] lg:pt-[121px]`}>
        <div className="p-4 md:p-8 lg:p-12 max-w-7xl">
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/assistent" element={<SimpleAssistentPage />} />
            <Route path="/datenbasis" element={<DatenbasisPage />} />
            <Route path="/strategie" element={<StrategieExpertePage />} />
            <Route path="/strategie-history" element={<StrategieHistoryPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/evergreen" element={<EvergreenPage />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/profile-optimizer" element={<ProfileOptimizerPage />} />
            <Route path="/social-media" element={<SocialMediaManagerPage />} />
            <Route path="/instagram" element={<InstagramManagerPage />} />
            <Route path="/moltbook" element={<MoltbookIntegrationPage />} />
            <Route path="/masterpost" element={<MasterpostPage />} />
            <Route path="/generator" element={<ContentGeneratorPage />} />
            <Route path="/content-tools" element={<ContentToolsPage />} />
            <Route path="/monthly-plan" element={<MonthlyPlanPage />} />
            {/* Alte Wochenansicht ist raus (leer in Produktion) — alte Lesezeichen
                landen im Monatsplan statt auf der 404-Seite. */}
            <Route path="/calendar" element={<Navigate to="/monthly-plan" replace />} />
            <Route path="/campaigns" element={<Campaigns />} />
            <Route path="/media" element={<MediaLibrary />} />
            <Route path="/setup-guide" element={<SetupGuidePage />} />
            <Route path="/kanale" element={<SocialConnectPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/planfinder" element={<PlanfinderPage />} />
            <Route path="/kontakt" element={<KontaktPage />} />
            <Route path="/website-optimierung" element={<WebsiteOptimierungPage />} />
            <Route path="/video" element={<MasterOnlyRoute><VideoPage /></MasterOnlyRoute>} />
            <Route path="/bonus" element={<BonusCenterPage />} />
            <Route path="/geld-verdienen" element={<GeldVerdienenPage />} />
            <Route path="/keyword-dm" element={<KeywordDMPage />} />
            <Route path="/instagram-comment-dm" element={<InstagramCommentDMPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="/nutzungsbedingungen" element={<NutzungsbedingungenPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/vertrauen" element={<VertrauenPage />} />
            <Route path="/link-in-bio" element={<LinkInBioPage />} />
            <Route path="/hilfe" element={<HilfePage />} />
            {/* 404 — unbekannte Route: ruhige Fehlerseite statt leerem Inhalt */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </div>
        <AppFooter />
      </main>

      {/* ── Onboarding-Wizard (volle Bildschirmüberlagerung) ── */}
      {showOnboarding && (
        <OnboardingWizard
          onComplete={() => {
            try { sessionStorage.setItem("ff_onboarding_dismissed", "1"); } catch (_) {}
            setShowOnboarding(false);
          }}
          onSkip={() => {
            try { sessionStorage.setItem("ff_onboarding_dismissed", "1"); } catch (_) {}
            setShowOnboarding(false);
          }}
        />
      )}
    </>
  );
}

// Main App
function App() {
  return (
    <div className="App min-h-screen bg-stone-50">
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <ScrollToHash />
        <MainLayout />
      </BrowserRouter>
    </div>
  );
}

export default App;

// Re-export Modal Components for backwards compatibility (used by MonthlyPlanPage)
export { FacebookPreviewModal, LinkedInPreviewModal, OutputFormatModal, TrendResearchModal, ContentRow } from './components/PostModals';
